import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { FileUploadItem } from '@/types/payroll-validator';

interface FileUploaderProps {
  files: FileUploadItem[];
  onFilesAdded: (files: File[]) => void;
  onFileRemove: (fileId: string) => void;
  acceptedFileTypes?: string[];
  maxFiles?: number;
  maxFileSize?: number;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  files,
  onFilesAdded,
  onFileRemove,
  acceptedFileTypes = ['.csv', '.xlsx', '.xls', '.pdf', '.html', '.htm'],
  maxFiles = 10,
  maxFileSize = 10 * 1024 * 1024, // 10MB
}) => {
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const rejectionReasons = rejectedFiles.map((file) => {
          if (file.errors[0]?.code === 'file-too-large') {
            return `${file.file.name}: File is too large (max ${maxFileSize / (1024 * 1024)}MB)`;
          }
          if (file.errors[0]?.code === 'file-invalid-type') {
            return `${file.file.name}: Invalid file type`;
          }
          return `${file.file.name}: ${file.errors[0]?.message}`;
        });
        setError(rejectionReasons.join(', '));
        return;
      }

      if (files.length + acceptedFiles.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      onFilesAdded(acceptedFiles);
    },
    [files, onFilesAdded, maxFiles, maxFileSize]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
      'text/html': ['.html', '.htm'],
    },
    maxSize: maxFileSize,
    maxFiles,
  });

  const getStatusIcon = (status: FileUploadItem['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'uploading':
        return <div className="h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />;
      default:
        return <FileText className="h-5 w-5 text-gray-400" />;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <Card>
        <CardContent className="p-6">
          <div
            {...getRootProps()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors duration-200
              ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'}
            `}
          >
            <input {...getInputProps()} />
            <Upload className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-lg font-medium text-gray-700 mb-2">
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              or click to browse
            </p>
            <p className="text-xs text-gray-400">
              Accepted: {acceptedFileTypes.join(', ')} • Max {maxFileSize / (1024 * 1024)}MB per file
            </p>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* File List */}
      {files.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Uploaded Files ({files.length})
            </h3>
            <div className="space-y-3">
              {files.map((fileItem) => (
                <div
                  key={fileItem.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-md"
                >
                  {/* Status Icon */}
                  <div className="flex-shrink-0">
                    {getStatusIcon(fileItem.status)}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileItem.file.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatFileSize(fileItem.file.size)}</span>
                      {fileItem.status === 'uploading' && (
                        <span>• {fileItem.progress}%</span>
                      )}
                      {fileItem.status === 'failed' && fileItem.error && (
                        <span className="text-red-600">• {fileItem.error}</span>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {fileItem.status === 'uploading' && (
                      <Progress value={fileItem.progress} className="mt-2 h-1" />
                    )}
                  </div>

                  {/* Remove Button */}
                  {(fileItem.status === 'pending' || fileItem.status === 'failed') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onFileRemove(fileItem.id)}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

